import { useEffect, useState } from 'react';
import type { Tables } from 'ErgoBot';
import { reportError } from 'Utils/reportError';
import type { FingeringDisplay, LiveFingering } from './display';
import { loadFingeringTables } from './tables';

type TablesState =
  | { status: 'loading' }
  | { status: 'ready'; tables: Tables }
  | { status: 'failed' };

/**
 * Live finger numbers for a lesson: the tables, once they have arrived, with
 * the density to draw at.
 *
 * `fingering` is undefined while live finger numbers are off, and also when the
 * tables could not be fetched: the lesson then runs as it does without them,
 * drawing whatever numbers the phrase files carry. `pending` is true while the
 * tables are on their way, so the scene can wait for them rather than start
 * without and finger only some of its phrases. `timed` asks the scene to say
 * how long fingering each phrase took, for the timing readout.
 */
export const useLiveFingering = (
  display: FingeringDisplay | undefined,
  timed: boolean = false,
): { fingering: LiveFingering | undefined; pending: boolean } => {
  const enabled = display !== undefined;
  const [tablesState, setTablesState] = useState<TablesState>({ status: 'loading' });

  useEffect(() => {
    if (!enabled) return undefined;
    let abandoned = false;
    loadFingeringTables()
      .then((tables) => {
        if (!abandoned) setTablesState({ status: 'ready', tables });
      })
      .catch((error: unknown) => {
        reportError(error, 'fingering.tables');
        if (!abandoned) setTablesState({ status: 'failed' });
      });
    return () => {
      abandoned = true;
    };
  }, [enabled]);

  return {
    fingering:
      enabled && tablesState.status === 'ready'
        ? { tables: tablesState.tables, display, timed }
        : undefined,
    pending: enabled && tablesState.status === 'loading',
  };
};
